/**
 * MapWithAiModal — the FE surface of the Mapping Plane. Opens for a connector,
 * asks the BE to auto-propose a canonical→vendor mapping (BE fetches live tools,
 * proposes via Claude, validates against ground truth, dry-run probes), and
 * shows the result for a Softknack admin to approve. The BE re-validates on
 * approve, so this screen is a reviewer, not a trust boundary.
 */
import React, { useEffect, useState } from 'react';
import { Pressable, ScrollView, StyleSheet, Text, View } from 'react-native';
import { colors, fontFamily, fontSize, fontWeight, radii } from '../theme';
import { Close } from '../icons';
import { Button, Modal, Spinner, ConfidenceBar } from '../components';
import { useBridge } from '../store/bridgeStore';
import { allInts, nameOf } from '../domain/catalog';
import { autoMapConnection, approveMapping, MappingAutoResult } from '../api/builder';

// Canonical tool the connector's product maps to. CRM→find_party is the live
// case today; extend as more canonical tools become probeable.
const CANONICAL_FOR_CATEGORY: Record<string, string> = {
  CRM: 'find_party',
  Scheduling: 'get_availability',
};

export default function MapWithAiModal() {
  const { state, actions } = useBridge();
  const id = state.mapAiId;
  if (!id) return null;

  const ints = allInts(state.adapters, state.metaOverrides);
  const a = (ints.find((i) => i.id === id) || {}) as any;
  const connId = (state as any).connBySlug?.[id] as string | undefined;
  const canonical = CANONICAL_FOR_CATEGORY[a.category] || 'find_party';

  const [loading, setLoading] = useState(true);
  const [result, setResult] = useState<MappingAutoResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [approving, setApproving] = useState(false);
  const [approved, setApproved] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    if (!connId) { setError('Connect this vendor before mapping.'); setLoading(false); return; }
    setLoading(true); setError(null); setResult(null); setApproved(null);
    autoMapConnection(connId, canonical)
      .then((r) => { if (!cancelled) { setResult(r); setLoading(false); } })
      .catch((e) => { if (!cancelled) { setError(e?.message || 'Auto-map failed'); setLoading(false); } });
    return () => { cancelled = true; };
  }, [connId, canonical]);

  const onApprove = async () => {
    if (!connId || !result) return;
    setApproving(true);
    try {
      const res = await approveMapping(connId, canonical, result.draft);
      setApproved(res.version_id);
      actions.toastRaw(`Mapping approved · ${canonical} → ${result.draft.vendor_tool}`, 'success');
    } catch (e: any) {
      actions.toastRaw(e?.message || 'Approve failed', 'danger');
    } finally {
      setApproving(false);
    }
  };

  const d = result?.draft;
  const confPct = d ? `${Math.round(d.confidence * 100)}%` : '0%';
  const confColor = d && d.confidence >= 0.7 ? colors.success : d && d.confidence >= 0.4 ? colors.warn : colors.danger;

  return (
    <Modal width={560} zIndex={75} onClose={actions.closeMapAi}>
      <View style={styles.header}>
        <View style={[styles.avatar, { backgroundColor: a.color || '#0a8a87' } as any]}><Text style={styles.avatarText}>{a.initials}</Text></View>
        <View style={{ flex: 1 }}>
          <Text style={styles.name}>{a.name || nameOf(id, ints)}</Text>
          <Text className="t-mono" style={styles.sub}>Map with AI · {canonical}</Text>
        </View>
        <Pressable onPress={actions.closeMapAi}><Close size={18} color={colors.ink3} /></Pressable>
      </View>

      <ScrollView style={{ maxHeight: '64vh' as any }} contentContainerStyle={{ padding: 20 }}>
        <View style={styles.infoBox}>
          <Text style={styles.infoText}>
            AI proposes which vendor tool implements <Text style={styles.mono}>{canonical}</Text>, validated against the vendor's real tools and dry-run probed. Review and approve to publish the mapping.
          </Text>
        </View>

        {loading && (
          <View style={styles.center}><Spinner /><Text style={styles.dim}>Proposing & probing against the live vendor…</Text></View>
        )}
        {error && !loading && <View style={styles.errBox}><Text style={styles.errText}>{error}</Text></View>}

        {d && !loading && (
          <>
            <Row label="Vendor tool">
              <Text className="t-mono" style={styles.val}>{d.vendor_tool ?? '— no fit —'}</Text>
            </Row>
            <Row label="Confidence">
              <View style={{ flex: 1, gap: 4 }}>
                <View style={{ width: 160 }}><ConfidenceBar width={confPct} color={confColor} /></View>
                <Text style={styles.dim}>{confPct} · {d.proposer}</Text>
              </View>
            </Row>
            <Row label="Reasoning"><Text style={styles.reason}>{d.reasoning}</Text></Row>

            <Text style={styles.section}>Field mapping</Text>
            <View style={{ gap: 4 }}>
              {d.fields.map((f, i) => (
                <View key={i} style={styles.fieldRow}>
                  <Text className="t-mono" style={styles.fieldFrom}>{f.from ?? `const(${String(f.const)})`}</Text>
                  <Text style={styles.arrow}>→</Text>
                  <Text className="t-mono" style={styles.fieldTo}>{f.to}</Text>
                </View>
              ))}
              {d.fields.length === 0 && <Text style={styles.dim}>(no field rules)</Text>}
            </View>

            <Text style={styles.section}>Checks</Text>
            <Check ok={result!.validation.ok} label={result!.validation.ok ? 'Validation passed (grounded in real tools/fields)' : `Validation failed: ${result!.validation.errors.join('; ')}`} />
            {result!.probe ? (
              <Check ok={result!.probe.ok} label={`Live probe: ${result!.probe.detail}`} />
            ) : (
              <Text style={styles.dim}>Probe skipped (write tool or invalid draft).</Text>
            )}
          </>
        )}

        {approved && <View style={styles.okBox}><Text style={styles.okText}>Approved — published version {approved.slice(0, 8)}…</Text></View>}
      </ScrollView>

      <View style={styles.footer}>
        <Button label="Close" variant="ghost" onPress={actions.closeMapAi} />
        {(() => {
          const blocked = !d || !d.vendor_tool || !result?.validation.ok || approving || !!approved;
          return (
            <Button
              label={approving ? 'Approving…' : approved ? 'Approved' : 'Approve mapping'}
              onPress={blocked ? undefined : onApprove}
              height={36}
              disabledLook={blocked}
            />
          );
        })()}
      </View>
    </Modal>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <View style={styles.row}>
      <Text style={styles.rowLabel}>{label}</Text>
      <View style={{ flex: 1 }}>{children}</View>
    </View>
  );
}
function Check({ ok, label }: { ok: boolean; label: string }) {
  return (
    <View style={styles.checkRow}>
      <View style={[styles.dot, { backgroundColor: ok ? colors.success : colors.danger }]} />
      <Text style={[styles.checkText, { color: ok ? colors.ink : colors.danger }]}>{label}</Text>
    </View>
  );
}

const styles = StyleSheet.create({
  header: { padding: 18, paddingHorizontal: 20, borderBottomWidth: 1, borderBottomColor: colors.line, flexDirection: 'row', alignItems: 'center', gap: 12 },
  avatar: { width: 38, height: 38, borderRadius: 9, alignItems: 'center', justifyContent: 'center' },
  avatarText: { fontFamily: fontFamily.sans, fontWeight: fontWeight.semibold, color: '#fff' },
  name: { fontFamily: fontFamily.sans, fontWeight: fontWeight.semibold, fontSize: fontSize.lg, color: colors.ink },
  sub: { fontFamily: fontFamily.mono, fontSize: fontSize.xs, color: colors.ink3 },
  infoBox: { padding: 11, paddingHorizontal: 13, borderWidth: 1, borderColor: colors.infoLine, backgroundColor: colors.infoSoft, borderRadius: radii.r3, marginBottom: 16 },
  infoText: { fontFamily: fontFamily.sans, fontSize: fontSize.xs, color: colors.info, lineHeight: 18 },
  mono: { fontFamily: fontFamily.mono },
  center: { alignItems: 'center', gap: 10, paddingVertical: 28 },
  dim: { fontFamily: fontFamily.sans, fontSize: fontSize.xs, color: colors.ink3 },
  errBox: { padding: 12, borderWidth: 1, borderColor: colors.dangerLine, backgroundColor: colors.dangerSoft, borderRadius: radii.r3 },
  errText: { fontFamily: fontFamily.sans, fontSize: fontSize.sm, color: colors.danger },
  row: { flexDirection: 'row', alignItems: 'flex-start', gap: 12, paddingVertical: 7, borderBottomWidth: 1, borderBottomColor: colors.line },
  rowLabel: { width: 96, fontFamily: fontFamily.sans, fontSize: fontSize.xs, color: colors.ink3, textTransform: 'uppercase', letterSpacing: 0.4, paddingTop: 2 },
  val: { fontFamily: fontFamily.mono, fontSize: fontSize.md, color: colors.ink },
  reason: { fontFamily: fontFamily.sans, fontSize: fontSize.sm, color: colors.ink2, lineHeight: 19 },
  section: { fontFamily: fontFamily.sans, fontSize: fontSize.xs, textTransform: 'uppercase', letterSpacing: 0.5, color: colors.ink3, fontWeight: fontWeight.semibold, marginTop: 18, marginBottom: 8 },
  fieldRow: { flexDirection: 'row', alignItems: 'center', gap: 8, paddingVertical: 6, paddingHorizontal: 10, borderWidth: 1, borderColor: colors.line, borderRadius: radii.r2, backgroundColor: colors.paper2 },
  fieldFrom: { fontFamily: fontFamily.mono, fontSize: fontSize.sm, color: colors.ink2, flex: 1 },
  arrow: { color: colors.ink3 },
  fieldTo: { fontFamily: fontFamily.mono, fontSize: fontSize.sm, color: colors.ink, flex: 1, textAlign: 'right' },
  checkRow: { flexDirection: 'row', alignItems: 'flex-start', gap: 8, paddingVertical: 4 },
  dot: { width: 8, height: 8, borderRadius: 4, marginTop: 5 },
  checkText: { fontFamily: fontFamily.sans, fontSize: fontSize.sm, flex: 1, lineHeight: 18 },
  okBox: { marginTop: 16, padding: 11, borderWidth: 1, borderColor: colors.successLine, backgroundColor: colors.successSoft, borderRadius: radii.r3 },
  okText: { fontFamily: fontFamily.sans, fontSize: fontSize.sm, color: colors.success },
  footer: { padding: 14, paddingHorizontal: 20, borderTopWidth: 1, borderTopColor: colors.line, backgroundColor: colors.paper2, flexDirection: 'row', alignItems: 'center', justifyContent: 'flex-end', gap: 10 },
});
